/**
 * The decisions behind the project screens, as pure functions.
 *
 * A project is a label on some of a group's transactions (`server/projects.ts`
 * makes the argument), so almost everything a screen has to decide about one
 * is a question about a list: which projects to offer, which transactions a
 * checklist may tick, what a row that is about to be *moved* rather than
 * added should say, and what a sheet sends for the project when nobody
 * touched the picker. None of it needs a DOM, so it lives here, where
 * `tests/projects-client.test.ts` can run it.
 *
 * **Archived is locked**, and every function here says so the way the server
 * does: an archived project is offered to nothing, a transaction in one cannot
 * be ticked into another, and a sheet correcting such a transaction never
 * re-sends the project it is locked in — the server refuses even that.
 */

import type { Project, Transaction } from '../api'

/** The server's own ceiling (`MAX_PROJECT_NAME` in `server/projects.ts`). */
export const MAX_PROJECT_NAME = 100

/** Open ones and archived ones, each in the order the server sent them. */
export function partitionProjects(projects: readonly Project[]): {
  open: Project[]
  archived: Project[]
} {
  return {
    open: projects.filter((project) => project.archivedAt === null),
    archived: projects.filter((project) => project.archivedAt !== null),
  }
}

/**
 * Why this name will be refused, or `null`. The same rules the server applies
 * — trimmed, not empty, at most 100 characters, and not the name of another
 * *open* project in the group ignoring case — said before the request rather
 * than after it. `exceptId` is the project being renamed, which may keep its
 * own name.
 */
export function projectNameProblem(
  name: string,
  projects: readonly Project[],
  exceptId?: string,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Give it a name.'
  if (trimmed.length > MAX_PROJECT_NAME) {
    return `Keep it to ${MAX_PROJECT_NAME} characters.`
  }
  const taken = projects.find(
    (project) =>
      project.archivedAt === null &&
      project.id !== exceptId &&
      project.name.toLowerCase() === trimmed.toLowerCase(),
  )
  return taken ? `There is already an open project called “${taken.name}”.` : null
}

/** "3 expenses", or that there is nothing in it yet. */
export function describeProjectCount(count: number): string {
  if (count === 0) return 'Nothing in it yet'
  return `${count} ${count === 1 ? 'transaction' : 'transactions'}`
}

/**
 * What to say before archiving, or `null` when there is nothing to warn
 * about. Archiving locks a project; it does not settle it, and somebody who
 * archives one that still has money owing in it is putting away a debt
 * nobody has paid — so that is asked about first.
 */
export function archiveWarning(project: Pick<Project, 'settled'>): string | null {
  if (project.settled) return null
  return (
    'Balances in this project are not zero. Archiving it locks what is in it; ' +
    'it does not settle anything, and the money still counts in the group.'
  )
}

/** Each project by id, archived ones included — what a row's chip is named from. */
export function projectsById(projects: readonly Project[]): Map<string, Project> {
  return new Map(projects.map((project) => [project.id, project]))
}

/**
 * The project picker on the expense sheet.
 *
 * `options` are the group's open projects; an archived one is never offered.
 * `locked` is set when the transaction being corrected sits in an archived
 * project — the server will not move it out of one, so the sheet shows where
 * it is instead of a control that could only be refused.
 */
export function projectPicker(
  projects: readonly Project[],
  currentId: string | null,
): { options: Project[]; locked: Project | null } {
  const current = currentId === null ? undefined : projects.find((p) => p.id === currentId)
  return {
    options: projects.filter((project) => project.archivedAt === null),
    locked: current && current.archivedAt !== null ? current : null,
  }
}

/**
 * What the sheet puts in `ExpenseDraft.projectId`.
 *
 * A new expense sends the project chosen, or nothing for none. A correction
 * sends only a *change*: an unchanged project is left out (`undefined`),
 * because re-sending even the same id is refused when that project has since
 * been archived — and an edit to the amount should not fail over a label
 * nobody touched. A choice that is no longer one of `options` (archived, or
 * from another group, since the sheet was opened) counts as none.
 */
export function projectIdToSend({
  chosen,
  initial,
  editing,
  options,
}: {
  chosen: string | null
  initial: string | null
  editing: boolean
  options: readonly Pick<Project, 'id'>[]
}): string | null | undefined {
  const valid = chosen !== null && options.some((option) => option.id === chosen) ? chosen : null
  if (!editing) return valid ?? undefined
  return chosen === initial || valid === initial ? undefined : valid
}

/** One line of the "add existing transactions" checklist. */
export type PickerRow = {
  transaction: Transaction
  /** The project it is in now, if any — so that ticking it visibly *moves* it. */
  current: Project | null
  /** In an archived project, which nothing may take a transaction out of. */
  locked: boolean
  /** What the row says about where it is now, or `null` when it is in no project. */
  note: string | null
}

/**
 * The checklist of a group's recent transactions to put in a project.
 *
 * Rows already in `targetId` are left out — there is nothing to add — and so
 * are rows still being filed, which have no id on the server yet. Every other
 * row is listed, including one in another project, with that project named:
 * ticking it moves it, and a move nobody can see coming is the one thing this
 * list must not do. A row in an archived project is listed but locked.
 */
export function pickerRows(
  transactions: readonly Transaction[],
  projects: readonly Project[],
  targetId: string | null = null,
): PickerRow[] {
  const byId = projectsById(projects)
  return transactions
    .filter((row) => row.pending !== true && (targetId === null || row.projectId !== targetId))
    .map((transaction) => {
      const current = transaction.projectId ? (byId.get(transaction.projectId) ?? null) : null
      const locked = current !== null && current.archivedAt !== null
      const note =
        current === null
          ? null
          : locked
            ? `In ${current.name}, which is archived — unarchive it to move this`
            : `In ${current.name} — ticking it moves it here`
      return { transaction, current, locked, note }
    })
}

/** The ids ticked, in the list's own order, never a locked one. */
export function tickedIds(rows: readonly PickerRow[], ticked: ReadonlySet<string>): string[] {
  return rows
    .filter((row) => !row.locked && ticked.has(row.transaction.id))
    .map((row) => row.transaction.id)
}

/** "Moves 2 from other projects", when ticking would — or `null`. */
export function describeMoves(rows: readonly PickerRow[], ticked: ReadonlySet<string>): string | null {
  const moving = rows.filter(
    (row) => !row.locked && row.current !== null && ticked.has(row.transaction.id),
  )
  if (moving.length === 0) return null
  const from = [...new Set(moving.map((row) => row.current!.name))]
  return `${moving.length === 1 ? 'One' : moving.length} ticked ${
    moving.length === 1 ? 'is' : 'are'
  } already in ${from.join(', ')}, and will move out of ${from.length === 1 ? 'it' : 'them'}.`
}

/**
 * Settling up inside a project: whether the project's plan gets Settle up
 * buttons, and the one line said about it instead when it does not.
 *
 * `canSettle` is false for an archived project — it is locked, and a payment
 * tagged to it would be refused (`project_archived`) — and in an archived
 * group, which takes no new money at all. An archived project that still has
 * money owing in it says how to settle it: unarchive it first.
 *
 * `note` is the *Settled* suggestion when the project nets to zero with
 * something in it and is still open: nothing is owed in it any more, so it
 * is ready to be put away. An empty project is settled too, trivially, and
 * says nothing — suggesting somebody archive a project they have only just
 * made is noise.
 */
export function projectSettleUp(
  project: Pick<Project, 'settled' | 'archivedAt' | 'transactionCount'>,
  groupArchived: boolean,
): { canSettle: boolean; note: string | null } {
  const locked = project.archivedAt !== null
  const canSettle = !locked && !groupArchived && !project.settled
  if (project.settled) {
    return {
      canSettle: false,
      note:
        project.transactionCount > 0 && !locked
          ? 'Settled — everybody in this project is square. Archive it to put it away.'
          : null,
    }
  }
  if (locked) {
    return {
      canSettle,
      note: 'Archived projects are locked, so there is no Settle up here. Unarchive it to settle it.',
    }
  }
  return { canSettle, note: null }
}
